import { useMemo } from 'react';

import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { scopeDpV2SuggestionsToFirstDeathClient } from '@/modules/aiDP/aidp.utils';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useTestamentaryEntitySuggestionsContextIfAvailable } from '@/modules/aiSuggestions/context/TestamentaryEntitySuggestions.context';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind } from '@/types/schema';
import { pluralize } from '@/utils/formatting/strings';

interface TestamentaryEntityAISuggestionsCountBadgeProps {
  section: EditTestamentaryEntitySection;
}

/**
 * Displays the count of unacknowledged suggestions for a given section of a testamentary entity.
 */
export function TestamentaryEntityAISuggestionsCountBadge({
  section,
}: TestamentaryEntityAISuggestionsCountBadgeProps) {
  const subtrustAISuggestionsEnabled = useFeatureFlag(
    'aikg_v2_associations_poc'
  );
  const canEdit = useCanEditHouseholdData();
  const suggestionsContext =
    useTestamentaryEntitySuggestionsContextIfAvailable();
  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();
  const { suggestionsByKind: dpSuggestionsByKind } =
    useDispositiveProvisionSuggestionsContext();
  const { primaryClients } = useHouseholdDetailsContext();

  const unacknowledgedCount = useMemo(() => {
    // Dispositions are sourced from a separate DP suggestions provider, not
    // from the SUBTRUST_* V2 testamentary-entity suggestions provider, so we
    // count them via the DP context. Scope DPv2 to the primary first-death
    // client to match the consolidated count shown on the entity-level card
    // (per-death-order counts surface separately within the dispositions form).
    if (section === EditTestamentaryEntitySection.DISPOSITIONS) {
      const primaryDeathOrderClientId = primaryClients?.[0]?.id;
      const scopedDpSuggestionsByKind = primaryDeathOrderClientId
        ? scopeDpV2SuggestionsToFirstDeathClient(
            dpSuggestionsByKind,
            primaryDeathOrderClientId
          )
        : dpSuggestionsByKind;

      const dpSuggestions =
        scopedDpSuggestionsByKind[AiSuggestionKind.DpRecipientV2] ?? [];

      return dpSuggestions.filter((s) => !acknowledgedSuggestions[s.id]).length;
    }

    if (!suggestionsContext) return 0;

    const suggestions =
      suggestionsContext.getCurrentSectionSuggestions(section);

    // Filter out acknowledged suggestions
    return suggestions.filter((s) => !acknowledgedSuggestions[s.id]).length;
  }, [
    acknowledgedSuggestions,
    dpSuggestionsByKind,
    primaryClients,
    section,
    suggestionsContext,
  ]);

  if (!subtrustAISuggestionsEnabled || !canEdit || !unacknowledgedCount) {
    return null;
  }

  return (
    <Badge
      variant={BadgeVariants.Success}
      display={pluralize({
        count: unacknowledgedCount,
        singular: 'suggestion',
      })}
    />
  );
}
